import { useEffect, useLayoutEffect, useRef } from 'react';
import * as axios from 'axios';
import { toast } from 'sonner';
import i18n from '@/lib/i18n';
import { useAuth } from '@/lib/auth/use-auth';
import { goToTenant } from '@/lib/tenant/tenant-navigation';
import { pickDefaultTenant } from '@/lib/tenant/tenant-grouping';
import { authControllerGetSession } from '@/generated/api/endpoints/auth/auth';
import { AvailableTenantDtoAccess } from '@/generated/api/models';

/**
 * A revoked data-sharing agreement only surfaces as a 403/404 on the next request —
 * watch for that, re-check reachability, bounce to a home tenant if dropped.
 * docs/arc42/grant-ledger.md, "Revocation propagation (frontend)".
 */
export function RevocationWatcher() {
  const { currentTenant, availableTenants } = useAuth();

  const currentTenantRef = useRef(currentTenant);
  const accessibleRef = useRef(availableTenants);
  const handlingRef = useRef(false);

  useLayoutEffect(() => {
    currentTenantRef.current = currentTenant;
    accessibleRef.current = availableTenants;
  });

  useEffect(() => {
    const interceptorId = axios.default.interceptors.response.use(
      response => response,
      async (error: axios.AxiosError) => {
        await maybeBounce(error);
        return await Promise.reject(error);
      },
    );

    return () => {
      axios.default.interceptors.response.eject(interceptorId);
    };

    async function maybeBounce(error: axios.AxiosError): Promise<void> {
      const status = error.response?.status;
      if (status !== 403 && status !== 404) {
        return;
      }

      const active = currentTenantRef.current;
      if (!active) {
        return;
      }

      // Only a data-sharing context can be revoked out from under the user; an
      // in-tenant permission denial is a normal 403 and must not bounce them.
      const activeEntry = accessibleRef.current.find(tenant => tenant.id === active.id);
      if (activeEntry?.access !== AvailableTenantDtoAccess['data-sharing']) {
        return;
      }

      if (handlingRef.current) {
        return;
      }
      handlingRef.current = true;
      try {
        const fresh = await authControllerGetSession();
        const tenants = fresh.data.availableTenants;
        const stillReachable = tenants.some(tenant => tenant.id === active.id);
        if (stillReachable) {
          return;
        }

        const home = pickDefaultTenant(tenants);
        if (home) {
          toast.info(i18n.t('auth:revocation.toast', { tenant: active.name, home: home.name }));
          goToTenant(home.slug);
        }
      } catch {
        // Session gone entirely (logged out) — nothing to bounce to.
      } finally {
        handlingRef.current = false;
      }
    }
  }, []);

  return null;
}
